import type { ReactNode, Ref } from 'react';
import {
  createDisplayAnswersWithIndices,
  type Answerable,
  type AnswerShuffleSeedSource,
} from '@/utils/arrayUtils';
import type { DisplaySlot } from '@/lib/quiz/emailAnswerIntent';

export type NewsletterEmailQuestionData = Answerable & AnswerShuffleSeedSource & { stem: string };

export type NewsletterEmailAnswer = {
  text: string;
  index: number;
  displaySlot: DisplaySlot;
};

// eslint-disable-next-line react-refresh/only-export-components
export function getNewsletterEmailAnswers(
  question: NewsletterEmailQuestionData
): NewsletterEmailAnswer[] {
  return createDisplayAnswersWithIndices(question).map((answer, displaySlot) => ({
    ...answer,
    displaySlot: displaySlot as DisplaySlot,
  }));
}

type NewsletterEmailQuestionProps = {
  question: NewsletterEmailQuestionData;
  onAnswerSelect: (answer: NewsletterEmailAnswer) => void;
  getAnswerLabel?: (text: string) => string;
  onAnswerIntent?: () => void;
  correctAnswerButtonRef?: Ref<HTMLButtonElement>;
  prompt?: ReactNode;
};

export function NewsletterEmailQuestion({
  question,
  onAnswerSelect,
  getAnswerLabel,
  onAnswerIntent,
  correctAnswerButtonRef,
  prompt,
}: NewsletterEmailQuestionProps) {
  return (
    <div
      data-testid="newsletter-demo-trivia-card"
      className="newsletter-demo-trivia-card mt-4 rounded-xl border border-outline-default bg-raised shadow-default"
    >
      <p className="newsletter-demo-question leading-snug font-bold text-glyph-primary">
        {question.stem}
      </p>
      {prompt}
      <div className="mt-3 grid gap-2">
        {getNewsletterEmailAnswers(question).map((answer) => (
          <button
            key={answer.index}
            ref={answer.index === 0 ? correctAnswerButtonRef : undefined}
            type="button"
            aria-label={getAnswerLabel?.(answer.text)}
            onFocus={onAnswerIntent}
            onPointerEnter={onAnswerIntent}
            onTouchStart={onAnswerIntent}
            onClick={() => onAnswerSelect(answer)}
            className={`newsletter-demo-answer min-h-11 touch-manipulation rounded-lg border border-outline-default bg-raised px-3 py-2 text-left leading-snug text-glyph-primary transition hover:border-accent-primary hover:bg-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary ${
              answer.index === 0 ? 'newsletter-demo-correct-answer-button' : ''
            }`}
          >
            {answer.text}
          </button>
        ))}
      </div>
    </div>
  );
}
